<script setup>
import { ref, onMounted, computed, defineProps } from 'vue';
import { useStore } from 'vuex';
import { fetchResources } from '@/connectors/ras';
import Previewer from '@/components/Previewer.vue';

const props = defineProps({ item: { type: Object, required: true } });

const store = useStore();

const currentModel = ref(null);
const loading = ref(false);
const currentCI = computed(() => props.item);

onMounted(async () => {
  loading.value = true;

  try {
    if (currentCI.value.revisions[0]?.models?.length) {
      const {
        data: {
          objects: [model],
        },
      } = await fetchResources(
        { limit: 1, offset: 0, ids: [currentCI.value.revisions[0].models[0]] },
        { resourceType: 'mesh', request: store.state.users.user.session.request },
      );
      currentModel.value = model;
    }
  } finally {
    loading.value = false;
  }
});
</script>

<template>
  <section class="ci-overview">
    <h2 class="col-span-3 flex flex-col">
      <small class="ci-overview__namespace text-xs text-brand-700"> {{ currentCI.namespace }} </small>
      <span class="ci-overview__name text-2xl">{{ currentCI.name }} </span>
      <div class="mb-2 flex items-baseline gap-2">
        <span class="text-xs">Created By</span>
        <span class="ci-overview__created-by text-sm">{{ currentCI.created_by }}</span> |
        <span class="text-xs">Created At</span>
        <span class="ci-overview__created-at text-sm">
          {{ new Date(currentCI.created_at).toLocaleString('en-us') }}
        </span>
      </div>
    </h2>

    <p class="col-span-3 w-full flex-grow rounded-lg bg-gray-50 p-2">{{ currentCI.description || '-' }}</p>

    <div v-if="currentModel" class="col-span-1 flex flex-col">
      <previewer
        :id="`${currentCI.namespace}-${currentCI.name}-previewer`"
        :key="`${currentCI.namespace}-${currentCI.name}-previewer`"
        :draft="currentCI.revisions[0]"
        class="w-full"
      />
      <router-link
        :to="`/configurable-items/${currentCI.namespace}:${currentCI.name}/draft`"
        class="text-brand-600 underline"
      >
        Settings
      </router-link>
    </div>

    <div
      class="ci-overview__model grid-row-4 col-span-2 grid grid-cols-3 self-start rounded-xl bg-white p-4"
      :loading="{ 'opacity-60': loading }"
    >
      <h2 class="col-span-4 text-lg">Model</h2>
      <span class="ci-overview__model__name col-span-3"> {{ currentModel?.name || '-' }} </span>
      <span class="ci-overview__model__short-id self-start justify-self-end text-xs">
        {{ currentModel?.shortId || '-' }}
      </span>
      <span class="ci-overview__model__version self-start justify-self-end text-xs">
        {{ currentModel?.version || '-' }}
      </span>
    </div>

    <section class="col-span-2 flex flex-col gap-2">
      <h2 class="text-lg">Revisions</h2>

      <ul class="m-0 flex list-none flex-col gap-y-2 p-0">
        <li
          v-for="{ revision, created_at, created_by } in currentCI.revisions"
          :key="revision"
          class="ci-overview__revision flex justify-between gap-1 border-b-2 py-2"
        >
          <span class="ci-overview__revision__created-at"> {{ created_at.toLocaleString('en-us') }} </span>
          <span class="ci-overview__revision__created-by text-sm text-gray-900">
            <small> by </small> {{ created_by }}</span
          >
        </li>
      </ul>
    </section>
  </section>
</template>

<style scoped lang="postcss">
.ci-overview {
  @apply grid h-full w-full grid-cols-4 gap-6 rounded-l-2xl p-6;
  grid-template-rows: 5.2rem auto auto;
}
</style>
